import { Mail, Phone, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const KIND_LABELS = {
  contact: "Contact", tour: "Tour request", pcs: "PCS plan", buy: "Home search", sell: "Selling",
  "market-analysis": "Market analysis", "pre-approval": "Pre-approval", career: "Careers", "vacation-sell": "Vacation home",
  alerts: "New-listing alerts", rent: "Rental search", "rental-management": "Rent out my home", callback: "Call me now",
};

const TOPIC_LABELS = {
  "home-value": "Home value", military: "Everything Military", luxury: "Luxury", "active-adult": "Active adult",
  rentals: "Rentals", "first-time-buyer": "First-time buyer", financing: "Grants & pre-approval", "ai-concierge": "AI concierge",
};

const STATUS_STYLES = { new: "bg-brass text-ink", contacted: "bg-neutral-200 text-ink", closed: "bg-neutral-100 text-neutral-500" };

const Detail = ({ label, value }) =>
  value ? (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</dt>
      <dd className="text-[14px] text-ink">{value}</dd>
    </div>
  ) : null;

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

/** One website enquiry with status control and delete. */
export const EnquiryCard = ({ e, baseName, onStatus, onDelete }) => (
  <article className="rounded-md border border-neutral-200 bg-white p-5" data-testid={`enquiry-card-${e.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${STATUS_STYLES[e.status]}`}>{e.status}</span>
          <span className="rounded-full border border-neutral-300 px-2.5 py-0.5 text-[12px] text-neutral-700" data-testid={`enquiry-kind-${e.id}`}>{KIND_LABELS[e.kind] || e.kind}</span>
          <span className="text-[12px] text-neutral-500">{when(e.created_at)}</span>
        </div>
        <h3 className="mt-2 text-lg font-medium text-ink" data-testid={`enquiry-name-${e.id}`}>{e.first_name} {e.last_name}</h3>
        <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-[14px]">
          {e.email && <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1.5 text-ink underline-offset-4 hover:underline" data-testid={`enquiry-email-${e.id}`}><Mail className="h-3.5 w-3.5" />{e.email}</a>}
          {e.phone && <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1.5 text-ink underline-offset-4 hover:underline" data-testid={`enquiry-phone-${e.id}`}><Phone className="h-3.5 w-3.5" />{e.phone}</a>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Select value={e.status} onValueChange={(v) => onStatus(e.id, v)}>
          <SelectTrigger className="h-9 w-[140px] rounded-full text-[13px]" data-testid={`enquiry-status-${e.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <button onClick={() => onDelete(e.id)} aria-label="Delete enquiry" data-testid={`enquiry-delete-${e.id}`}
          className="grid h-9 w-9 place-items-center rounded-full border border-neutral-300 text-neutral-600 transition-colors hover:border-red-500 hover:text-red-600">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
    <dl className="mt-4 grid gap-3 sm:grid-cols-3">
      <Detail label="Installation" value={baseName} />
      <Detail label="Branch" value={e.branch} />
      <Detail label="Move" value={e.move_direction} />
      <Detail label="Report date" value={e.report_date} />
      <Detail label="Tour date" value={e.preferred_date} />
      <Detail label="Listing" value={e.listing_key} />
      <Detail label="Property" value={[e.property_address, e.property_type].filter(Boolean).join(" · ")} />
      <Detail label="From guide" value={TOPIC_LABELS[e.topic] || e.topic} />
    </dl>
    {e.message && <p className="mt-4 whitespace-pre-line rounded-md bg-mist p-3 text-[14px] leading-relaxed text-neutral-700" data-testid={`enquiry-message-${e.id}`}>{e.message}</p>}
  </article>
);
